import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import FlexBox from 'Components/FlexBox';
import CountrySelectField from 'Components/Forms/CountrySelectField';
import TextInputField from 'Components/Forms/TextInputField';
import { ThemeButton } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import PartnerAutocompleteWrapper from 'FeatureModules/Activity/Components/Forms/PartnerAutocompleteWrapper';
import RawMaterialSelection from 'FeatureModules/ComponentChain/Components/Forms/RawMaterialSelection';
import { IDatasetFormValue } from 'FeatureModules/DataImport/schemas/geoUpload.schema';
import { Form, FormikProps } from 'formik';
import isEqual from 'lodash/isEqual';
import React, { FC, useEffect } from 'react';

interface IDatasetFormProps extends FormikProps<IDatasetFormValue> {
  disableOwnerSelection?: boolean;
  isTableView?: boolean;
  hideSubmitButton?: boolean;
  submitButtonText?: string;
  onTableSettingsClick?: () => void;
  onFormChange?: () => void;
}

const SelectContainer = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.custom.themeColors.grayScale[20]}`,
  background: theme.custom.themeColors.white,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'flex-end',
  width: '100%',
}));

const DropdownContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  gap: theme.spacing(2),
  width: '100%',
  '& > div': {
    flex: 3,
  },
}));

const DatasetForm: FC<IDatasetFormProps> = ({
  values,
  initialValues,
  isValid,
  isSubmitting,
  isTableView,
  disableOwnerSelection,
  hideSubmitButton,
  submitButtonText,
  onTableSettingsClick,
  onFormChange,
}) => {
  useEffect(() => {
    if (!isEqual(values, initialValues)) {
      onFormChange?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, initialValues]);

  return (
    <Form>
      <Box display="flex" flexDirection="column" gap={2}>
        <SelectContainer>
          <DropdownContainer>
            <TextInputField
              name="title"
              isRequired
              helperText="Add a name to your geo-dataset that helps you and your colleagues remember to what origin this dataset refers to. Using specific region names could be helpful."
              label="Title"
              size="small"
              placeholder='E.g. "Origin geo-data Ghana"'
            />
            <RawMaterialSelection
              isRequired
              multiple={false}
              name="rawMaterialId"
              size="small"
              helperText="Select the commodity that is sourced from this origin."
            />
            <CountrySelectField
              isRequired
              showCountryRisk
              name="countryCode"
              label="Country of origin"
              helperText="Select the country where this origin geo-data is located. You can only have one country per origin geo-dataset."
            />
            <PartnerAutocompleteWrapper
              isRequired
              name="ownedBy"
              label="Owner"
              disabled={disableOwnerSelection}
              helperText="Select an owner of this origin geo-dataset. This would optimally be a supplier that is involved in the supply chain connected to this origin. You can also select your own company."
            />
          </DropdownContainer>

          <FlexBox justifyContent="flex-end" gap={2}>
            {isTableView && (
              <ThemeButton
                variant="contained"
                color="BLUE_ICE"
                onClick={onTableSettingsClick}
                startIcon={<Icon name="settings-filled" size="small" />}
                data-tut="onboarding_tour_data_import_settings"
              >
                Table settings
              </ThemeButton>
            )}

            {!hideSubmitButton && (
              <ThemeButton
                type="submit"
                disabled={!isValid || (!isTableView && isEqual(values, initialValues))}
                loading={isSubmitting}
              >
                {submitButtonText ? submitButtonText : isTableView ? 'Save & verify' : 'Save'}
              </ThemeButton>
            )}
          </FlexBox>
        </SelectContainer>
      </Box>
    </Form>
  );
};

export default DatasetForm;
